import React, { useState, useEffect } from 'react';
import {
  getAllPortfolios,
  saveCustomPortfolio,
  updatePortfolio,
  deletePortfolio,
  portfolioToForm,
  emptyPortfolioForm,
  isCustomPortfolio,
} from '../../common/portfolioStore';
import { inputStyle, cardStyle, formBoxStyle } from './adminShared';

function AdminPortfolio() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyPortfolioForm);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    refreshItems();
  }, []);

  async function refreshItems() {
    try {
      setLoading(true);
      setError('');
      setItems(await getAllPortfolios());
    } catch (err) {
      setError(err.message || 'Projeler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setForm(emptyPortfolioForm);
    setEditingId(null);
    setPreviewUrl('');
    setShowForm(false);
  }

  function openCreateForm() {
    setEditingId(null);
    setForm(emptyPortfolioForm);
    setPreviewUrl('');
    setShowForm(true);
  }

  function openEditForm(item) {
    setEditingId(item.id);
    setForm(portfolioToForm(item));
    setPreviewUrl(item.coverImg || '');
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'coverImg') setPreviewUrl(value);
  }

  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setError('Geçersiz dosya türü. Sadece JPG, PNG, GIF, WEBP desteklenir.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Dosya boyutu 5MB\'ı aşamaz.');
      return;
    }

    const formData = new FormData();
    formData.append('image', file);

    try {
      setUploading(true);
      setError('');
      const res = await fetch('/api/upload', {
        method: 'POST',
        headers: { 'X-Admin-Password': 'kanus2024' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Yükleme başarısız.');
      setForm((prev) => ({ ...prev, coverImg: data.url }));
      setPreviewUrl(data.url);
    } catch (err) {
      setError(err.message || 'Fotoğraf yüklenemedi.');
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title || !form.url) return;

    try {
      setError('');
      if (editingId) {
        await updatePortfolio(editingId, form);
      } else {
        await saveCustomPortfolio(form);
      }
      resetForm();
      await refreshItems();
    } catch (err) {
      setError(err.message || 'Kayıt işlemi başarısız.');
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Bu projeyi silmek istediğinize emin misiniz?')) return;
    try {
      setError('');
      await deletePortfolio(id);
      if (editingId === id) resetForm();
      await refreshItems();
    } catch (err) {
      setError(err.message || 'Silme işlemi başarısız.');
    }
  }

  const customCount = items.filter((p) => isCustomPortfolio(p.id)).length;

  return (
    <>
      <div className="sec-head mb-50">
        <div className="d-flex align-items-center flex-wrap">
          <div>
            <span className="sub-title main-color mb-5">Yönetim Paneli</span>
            <h3 className="fw-600 fz-40">Portfolio Yönetimi</h3>
          </div>
          <div className="ml-auto d-flex align-items-center flex-wrap" style={{ gap: '12px' }}>
            <a href="/portfolio" className="butn butn-sm butn-bord radius-30">
              <span>Portfolio Görüntüle</span>
            </a>
            <button
              onClick={() => (showForm && !editingId ? resetForm() : openCreateForm())}
              className="butn butn-sm butn-bg main-colorbg radius-5"
              style={{ border: 'none', cursor: 'pointer' }}
            >
              <span className="text">
                {showForm && !editingId ? 'Formu Kapat' : '+ Yeni Proje'}
              </span>
            </button>
          </div>
        </div>
        <p className="p-color mt-15 fz-14">
          Toplam {items.length} proje ({customCount} admin tarafından eklendi)
        </p>
        {error && <p className="main-color mt-15 fz-14">{error}</p>}
        {loading && <p className="p-color mt-15 fz-14">Yükleniyor...</p>}
      </div>

      {showForm && (
        <div className="mb-60 p-40" style={formBoxStyle}>
          <div className="d-flex align-items-center mb-30">
            <h5 className="mb-0">
              {editingId ? 'Projeyi Düzenle' : 'Yeni Proje Ekle'}
            </h5>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="ml-auto p-color fz-14"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Düzenlemeyi İptal Et
              </button>
            )}
          </div>
          <form onSubmit={handleSubmit}>
            <div className="row lg-marg">
              <div className="col-lg-6">
                <div className="mb-20">
                  <label className="sub-title fz-12 mb-10 d-block">Proje Adı *</label>
                  <input
                    type="text"
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    placeholder="Proje başlığı"
                    required
                    style={inputStyle}
                  />
                </div>
                <div className="mb-20">
                  <label className="sub-title fz-12 mb-10 d-block">Alt Başlık</label>
                  <input
                    type="text"
                    name="titleHighlight"
                    value={form.titleHighlight}
                    onChange={handleChange}
                    placeholder="ör: Kurumsal Web Sitesi"
                    style={inputStyle}
                  />
                </div>
                <div className="mb-20">
                  <label className="sub-title fz-12 mb-10 d-block">Kategori</label>
                  <input
                    type="text"
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                    placeholder="ör: Kurumsal, E-Ticaret, Portfolyo"
                    style={inputStyle}
                  />
                </div>
                <div className="mb-20">
                  <label className="sub-title fz-12 mb-10 d-block">Site URL *</label>
                  <input
                    type="url"
                    name="url"
                    value={form.url}
                    onChange={handleChange}
                    placeholder="https://..."
                    required
                    style={inputStyle}
                  />
                </div>
                <div className="mb-20">
                  <label className="sub-title fz-12 mb-10 d-block">Kısa Açıklama</label>
                  <textarea
                    name="excerpt"
                    value={form.excerpt}
                    onChange={handleChange}
                    placeholder="Liste sayfasında görünecek kısa açıklama..."
                    rows={3}
                    style={{ ...inputStyle, resize: 'vertical' }}
                  />
                </div>
                <div className="mb-20">
                  <label className="sub-title fz-12 mb-10 d-block">Detay İçeriği</label>
                  <textarea
                    name="content"
                    value={form.content}
                    onChange={handleChange}
                    placeholder="Detay sayfasında görünecek tam açıklama. Her paragraf yeni satırdan başlasın."
                    rows={8}
                    style={{ ...inputStyle, resize: 'vertical' }}
                  />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="mb-20">
                  <label className="sub-title fz-12 mb-10 d-block">Kapak Fotoğrafı</label>

                  {/* Fotoğraf Yükleme Butonu */}
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      padding: '12px 16px',
                      borderRadius: '6px',
                      border: '2px dashed rgba(255,255,255,0.25)',
                      marginBottom: '12px',
                      background: 'rgba(255,255,255,0.04)',
                      transition: 'border-color 0.2s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)')}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)')}
                  >
                    <span style={{ fontSize: '20px' }}>📷</span>
                    <span className="p-color fz-13">
                      {uploading ? 'Yükleniyor...' : 'Bilgisayardan Fotoğraf Seç'}
                    </span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      style={{ display: 'none' }}
                    />
                  </label>

                  {/* Manuel URL girişi */}
                  <input
                    type="text"
                    name="coverImg"
                    value={form.coverImg}
                    onChange={handleChange}
                    placeholder="veya fotoğraf URL'si yapıştır (https://...)"
                    style={inputStyle}
                  />
                  {!form.coverImg && form.url && (
                    <p className="p-color fz-11 mt-5" style={{ opacity: 0.6 }}>
                      Boş bırakılırsa site URL'inden otomatik ekran görüntüsü alınır.
                    </p>
                  )}
                </div>

                {/* Önizleme */}
                {(previewUrl || form.url) ? (
                  <div
                    style={{
                      borderRadius: '8px',
                      overflow: 'hidden',
                      maxHeight: '220px',
                      border: '1px solid rgba(255,255,255,0.1)',
                      position: 'relative',
                    }}
                  >
                    <img
                      src={previewUrl || `https://image.thum.io/get/width/600/crop/800/${form.url}`}
                      alt="Önizleme"
                      style={{ width: '100%', objectFit: 'cover', display: 'block' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    {previewUrl && (
                      <button
                        type="button"
                        onClick={() => { setPreviewUrl(''); setForm((p) => ({ ...p, coverImg: '' })); }}
                        style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          background: 'rgba(0,0,0,0.7)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '50%',
                          width: '28px',
                          height: '28px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          lineHeight: '28px',
                          textAlign: 'center',
                        }}
                        title="Fotoğrafı kaldır"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ) : (
                  <div
                    style={{
                      borderRadius: '8px',
                      border: '1px dashed rgba(255,255,255,0.2)',
                      height: '200px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'rgba(255,255,255,0.3)',
                      fontSize: '14px',
                    }}
                  >
                    Fotoğraf seçince veya URL girilince önizleme görünür
                  </div>
                )}
              </div>
            </div>
            <div className="mt-30 d-flex align-items-center" style={{ gap: '12px' }}>
              <button
                type="submit"
                className="butn butn-sm butn-bg main-colorbg radius-5"
                style={{ border: 'none', cursor: 'pointer' }}
              >
                <span className="text">{editingId ? 'Güncelle' : 'Projeyi Ekle'}</span>
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="butn butn-sm butn-bord radius-30"
                  style={{ border: 'none', cursor: 'pointer' }}
                >
                  <span>İptal</span>
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      <div className="row lg-marg">
        {items.map((item) => (
          <div key={item.id} className="col-lg-4 col-md-6 mb-40">
            <div style={cardStyle}>
              {item.coverImg && (
                <div style={{ height: '160px', overflow: 'hidden' }}>
                  <img
                    src={item.coverImg}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              )}
              <div style={{ padding: '20px' }}>
                <span className="sub-title main-color fz-12">{item.category}</span>
                <h6 className="mt-10 mb-10">
                  {item.title} {item.titleHighlight}
                </h6>
                <p className="p-color fz-13 mb-10">{item.excerpt?.slice(0, 80)}...</p>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-color fz-12 d-block mb-20"
                  style={{ wordBreak: 'break-all' }}
                >
                  {item.url}
                </a>
                <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                  <a
                    href={`/portfolio-details/${item.id}`}
                    className="butn butn-sm butn-bord radius-30"
                  >
                    <span>Detay Sayfası</span>
                  </a>
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="butn butn-sm butn-bord radius-30"
                  >
                    <span>Siteyi Aç</span>
                  </a>
                  <button
                    onClick={() => openEditForm(item)}
                    className="butn butn-sm butn-bord radius-30"
                    style={{ border: 'none', cursor: 'pointer' }}
                  >
                    <span>Düzenle</span>
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="butn butn-sm radius-30"
                    style={{
                      border: '1px solid rgba(255,80,80,0.4)',
                      color: '#ff6b6b',
                      background: 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <span>Sil</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export default AdminPortfolio;
